/**
 * Certificate Management Component
 * ISO 27001 A.9.4.2 - Secure authentication mechanisms (mTLS)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  Plus,
  RefreshCw,
  Trash2,
  AlertTriangle,
  Key,
  Ban,
  Upload,
} from 'lucide-react';
import {
  useCertificates,
  useCertificateStats,
  useCertificatesExpiringSoon,
  useUploadCertificate,
  useRevokeCertificate,
  useDeleteCertificate,
} from '../hooks/useClientCertificates';
import { ClientCertificate } from '../api/client-certificate.api';
import { logger } from '@/utils/logger';

export function CertificateManagement() {
  const { t } = useTranslation();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState<ClientCertificate | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<ClientCertificate | null>(null);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    certificatePem: '',
    scopes: 'read',
    allowedIps: '',
    rateLimit: 1000,
  });
  const [revokeReason, setRevokeReason] = useState('');

  const { data: certificates, isLoading, refetch } = useCertificates({ includeExpired: true });
  const { data: stats } = useCertificateStats();
  const { data: expiring } = useCertificatesExpiringSoon(30);

  const uploadMutation = useUploadCertificate();
  const revokeMutation = useRevokeCertificate();
  const deleteMutation = useDeleteCertificate();

  const handleUpload = async () => {
    try {
      await uploadMutation.mutateAsync({
        name: uploadForm.name,
        description: uploadForm.description || undefined,
        certificatePem: uploadForm.certificatePem,
        scopes: uploadForm.scopes.split(',').map((s) => s.trim()),
        allowedIps: uploadForm.allowedIps
          ? uploadForm.allowedIps.split(',').map((s) => s.trim())
          : undefined,
        rateLimit: uploadForm.rateLimit,
      });
      setShowUploadModal(false);
      setUploadForm({
        name: '',
        description: '',
        certificatePem: '',
        scopes: 'read',
        allowedIps: '',
        rateLimit: 1000,
      });
    } catch (error) {
      logger.error('Upload failed:', error);
    }
  };

  const handleRevoke = async () => {
    if (!showRevokeModal || !revokeReason) return;
    try {
      await revokeMutation.mutateAsync({
        id: showRevokeModal.id,
        reason: revokeReason,
      });
      setShowRevokeModal(null);
      setRevokeReason('');
    } catch (error) {
      logger.error('Revoke failed:', error);
    }
  };

  const handleDelete = async () => {
    if (!showDeleteModal) return;
    try {
      await deleteMutation.mutateAsync(showDeleteModal.id);
      setShowDeleteModal(null);
    } catch (error) {
      logger.error('Delete failed:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isExpired = (cert: ClientCertificate) => new Date(cert.notAfter) < new Date();
  const isExpiringSoon = (cert: ClientCertificate) => {
    const thirtyDays = new Date();
    thirtyDays.setDate(thirtyDays.getDate() + 30);
    return new Date(cert.notAfter) < thirtyDays && !isExpired(cert);
  };

  const getStatusBadge = (cert: ClientCertificate) => {
    if (cert.revokedAt) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
          {t('admin.certificates.status.revoked')}
        </span>
      );
    }
    if (isExpired(cert)) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
          {t('admin.certificates.status.expired')}
        </span>
      );
    }
    if (!cert.isActive) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
          {t('admin.certificates.status.inactive')}
        </span>
      );
    }
    if (isExpiringSoon(cert)) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
          {t('admin.certificates.status.expiringSoon')}
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
        {t('admin.certificates.status.active')}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Key className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('admin.certificates.title')}</h2>
            <p className="text-sm text-gray-500">
              {t('admin.certificates.subtitle')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700"
          >
            <Plus className="w-4 h-4" />
            {t('admin.certificates.uploadCertificate')}
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="p-4 bg-white rounded-xl border border-gray-200">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-500">{t('admin.certificates.total')}</div>
          </div>
          <div className="p-4 bg-white rounded-xl border border-gray-200">
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <div className="text-sm text-gray-500">{t('admin.certificates.active')}</div>
          </div>
          <div className="p-4 bg-white rounded-xl border border-gray-200">
            <div className="text-2xl font-bold text-gray-600">{stats.expired}</div>
            <div className="text-sm text-gray-500">{t('admin.certificates.expired')}</div>
          </div>
          <div className="p-4 bg-white rounded-xl border border-gray-200">
            <div className="text-2xl font-bold text-red-600">{stats.revoked}</div>
            <div className="text-sm text-gray-500">{t('admin.certificates.revoked')}</div>
          </div>
          <div className="p-4 bg-white rounded-xl border border-gray-200">
            <div className="text-2xl font-bold text-blue-600">{stats.recentUsage}</div>
            <div className="text-sm text-gray-500">{t('admin.certificates.apiCalls30d')}</div>
          </div>
        </div>
      )}

      {/* Expiring Soon Alert */}
      {expiring && expiring.length > 0 && (
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
          <div className="flex items-center gap-2 text-orange-700">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">
              {t('admin.certificates.expiringAlert', { count: expiring.length })}
            </span>
          </div>
        </div>
      )}

      {/* Certificates List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t('admin.certificates.tableHeaders.certificate')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t('admin.certificates.tableHeaders.subject')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t('admin.certificates.tableHeaders.validity')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t('admin.certificates.tableHeaders.status')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t('admin.certificates.tableHeaders.usage')}
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                {t('admin.certificates.tableHeaders.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {certificates?.map((cert) => (
              <tr key={cert.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900">{cert.name}</div>
                      <div className="text-xs text-gray-500 font-mono">
                        {cert.thumbprint.substring(0, 16)}...
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-gray-900">{cert.subject}</div>
                  <div className="text-xs text-gray-500">{cert.issuer}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-gray-900">
                    {formatDate(cert.notBefore)} - {formatDate(cert.notAfter)}
                  </div>
                </td>
                <td className="px-4 py-3">{getStatusBadge(cert)}</td>
                <td className="px-4 py-3">
                  <div className="text-sm text-gray-900">{cert.usageCount} {t('admin.certificates.calls')}</div>
                  {cert.lastUsedAt && (
                    <div className="text-xs text-gray-500">
                      {t('admin.certificates.last')} {formatDate(cert.lastUsedAt)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    {!cert.revokedAt && cert.isActive && !isExpired(cert) && (
                      <button
                        onClick={() => setShowRevokeModal(cert)}
                        className="p-1 text-orange-600 hover:bg-orange-50 rounded"
                        title={t('admin.certificates.revokeModal.revoke')}
                      >
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => setShowDeleteModal(cert)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title={t('admin.certificates.deleteModal.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {certificates?.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  {t('admin.certificates.noCertificates')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 rounded-xl">
                  <Upload className="w-5 h-5 text-teal-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">{t('admin.certificates.uploadModal.title')}</h3>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.certificates.uploadModal.name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                  placeholder={t('admin.certificates.uploadModal.namePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.certificates.uploadModal.description')}
                </label>
                <input
                  type="text"
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                  placeholder={t('admin.certificates.uploadModal.descriptionPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.certificates.uploadModal.certificatePem')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={uploadForm.certificatePem}
                  onChange={(e) => setUploadForm({ ...uploadForm, certificatePem: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm font-mono"
                  rows={6}
                  placeholder="-----BEGIN CERTIFICATE-----
...
-----END CERTIFICATE-----"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.certificates.uploadModal.scopes')}
                </label>
                <input
                  type="text"
                  value={uploadForm.scopes}
                  onChange={(e) => setUploadForm({ ...uploadForm, scopes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                  placeholder="read, write"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.certificates.uploadModal.allowedIps')}
                </label>
                <input
                  type="text"
                  value={uploadForm.allowedIps}
                  onChange={(e) => setUploadForm({ ...uploadForm, allowedIps: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                  placeholder="192.168.1.1, 10.0.0.0/8"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.certificates.uploadModal.rateLimit')}
                </label>
                <input
                  type="number"
                  value={uploadForm.rateLimit}
                  onChange={(e) =>
                    setUploadForm({ ...uploadForm, rateLimit: parseInt(e.target.value) || 1000 })
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl"
              >
                {t('admin.certificates.uploadModal.cancel')}
              </button>
              <button
                onClick={handleUpload}
                disabled={!uploadForm.name || !uploadForm.certificatePem || uploadMutation.isPending}
                className="px-4 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 disabled:opacity-50"
              >
                {uploadMutation.isPending ? t('admin.certificates.uploadModal.uploading') : t('admin.certificates.uploadModal.upload')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-xl">
                  <Ban className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">{t('admin.certificates.revokeModal.title')}</h3>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                {t('admin.certificates.revokeModal.confirmMessage')}{' '}
                <span className="font-medium">{showRevokeModal.name}</span>{t('admin.certificates.revokeModal.cannotBeUndone')}
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.certificates.revokeModal.reason')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                  rows={3}
                  placeholder={t('admin.certificates.revokeModal.reasonPlaceholder')}
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRevokeModal(null);
                  setRevokeReason('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl"
              >
                {t('admin.certificates.revokeModal.cancel')}
              </button>
              <button
                onClick={handleRevoke}
                disabled={!revokeReason || revokeMutation.isPending}
                className="px-4 py-2 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50"
              >
                {revokeMutation.isPending ? t('admin.certificates.revokeModal.revoking') : t('admin.certificates.revokeModal.revoke')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-xl">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">{t('admin.certificates.deleteModal.title')}</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600">
                {t('admin.certificates.deleteModal.confirmMessage')}{' '}
                <span className="font-medium">{showDeleteModal.name}</span>{t('admin.certificates.deleteModal.cannotBeUndone')}
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl"
              >
                {t('admin.certificates.deleteModal.cancel')}
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? t('admin.certificates.deleteModal.deleting') : t('admin.certificates.deleteModal.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
